import React, { FC, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTheme } from '@mui/material/styles'

import Button from '@mui/material/Button'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'

import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

import Link from '../components/widgets/Link'
import SideMenu from '../components/widgets/SideMenu'
import ProjectSelector from '../components/rbac/ProjectSelector'
import UserOrgSelector from '../components/rbac/UserOrgSelector'
import UnimpersonateButton from '../components/auth/UnimpersonateButton'

import Window from '../components/dialog/Window'
import Snackbar from '../components/system/Snackbar'
import GlobalLoading from '../components/system/GlobalLoading'

import networkSelectors from '../store/selectors/network'
import projectSelectors from '../store/selectors/project'
import authSelectors from '../store/selectors/auth'
import rbacActions from '../store/modules/rbac'
import authActions from '../store/modules/auth'
import routerActions from '../store/modules/router'

import {
  TOPBAR_HEIGHT,
  APP_HEADER,
  APP_LOGO_HEARTS,
  APP_LOGO_TEXT,
  DRAWER_COLLAPSED_WIDTH,
} from '../settings'

import { getThemeConfig } from '../theme'

const impersonateData = localStorage.getItem('impersonate_existing_token')

const Layout: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const globalLoading = useSelector(networkSelectors.globalLoading)

  const loggingOut = useSelector(authSelectors.loggingOut)
  const title = useSelector(projectSelectors.title)
  const logo = useSelector(projectSelectors.logo)
  const menu = useSelector(projectSelectors.mainMenu)
  const userData = useSelector(authSelectors.data)
  const currentOrg = useSelector(projectSelectors.currentOrg)
  const currentProjectId = useSelector(projectSelectors.currentProjectId)
  const projectNeedsVerification = useSelector(projectSelectors.needVerifying)

  const showTitle = false
  
  const themeConfig = getThemeConfig()
  
  useEffect(() => {
    if(currentProjectId) {
      dispatch(rbacActions.loadProfiles())
    }
  }, [])

  // Add this line to check if impersonation is active
  const isImpersonating = !!impersonateData

  return (
    <Box
      sx={{
        p: 0,
        m: 0,
        display: 'flex',
        height: '100%',
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          minHeight: `${TOPBAR_HEIGHT}px`,
          height: `${TOPBAR_HEIGHT}px`,
          maxHeight: `${TOPBAR_HEIGHT}px`,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          ...themeConfig.getToolbarSx(),
        }}>
        <Toolbar
          disableGutters
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 2.5,
          }}
        >
          <Box
            component='div'
            onClick={() => {
              dispatch(routerActions.navigateTo('dashboard'))
            }}
            sx={{
              cursor: 'pointer',
              ml: 0,
              my: 'auto',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            { themeConfig.getLogo() }
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              height: '100%',
            }}
          >
          </Box>
            {
              logo && (
                <Box
                  pr={1}
                  m={'auto'}
                >
                  <Link
                    name="dashboard"
                  >
                    <Box
                      component={ 'img'}
                      src={ logo }
                      sx={{
                        m: 1,
                        maxHeight: '31.5px', // Reduced from 42px (25% smaller)
                        display: 'block',
                      }}
                    />
                  </Link>
                  
                </Box>
              )
            }
            {
              showTitle ? (
                <Box
                  m={'auto'}
                >
                  <Typography 
                    variant="h6" 
                    color="primary" 
                  >
                    { title }
                  </Typography>
                </Box>
              ) : (
                <Box
                  mx={1}
                  sx={{
                    minWidth: '300px', // Reduced from 400px
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <ProjectSelector />
                  <UserOrgSelector />
                  {isImpersonating && (
                    <UnimpersonateButton />
                  )}
                </Box>
              )
              
            }
        </Toolbar>
      </AppBar>
      {
        !loggingOut && (
          <Box
            sx={{
              mt: `${TOPBAR_HEIGHT}px`,
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Box
              sx={{
                flexGrow: 0,
                position: 'fixed',
                zIndex: 1000,
                
              }}
            >
              <SideMenu
                items={ menu }
              />
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                ml: `${DRAWER_COLLAPSED_WIDTH}px`,
                height: '100%',
              }}
            >
              { children }
            </Box>
          </Box>
        )
    }
      <GlobalLoading { ...globalLoading } />
      <Snackbar />
      {
        projectNeedsVerification && (
          <Window
            open
            title="Verify Email Address"
            size="sm"
          >
            <Typography>
              We have sent a verification email to <strong>{ userData.email }</strong>.
            </Typography>
            <Typography>
              Please verify your email address to continue.
            </Typography>
            <Button
              sx={{
                mt: 2,
                mr: 2,
              }}
              onClick={() => {
                dispatch(authActions.resendRegisterEmail())
              }}
            >
              Resend Email
            </Button>
            <br />
            <Button
              sx={{
                mt: 2,
              }}
              onClick={() => {
                dispatch(authActions.logout())
              }}
            >
              Logout
            </Button>
          </Window>
        )
      }
    </Box>
  )
}

export default Layout